import {useNavigate} from 'react-router-dom';
import {trpc} from '@common-web/lib/trpc';
import {Button} from '@common-web/components/ui/button';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {SCOPE_LABELS, type PermissionScope} from '@common/permissions';
import {ScopeBadge} from '@internal/components/roles-permissions/ScopeBadge';
import {SCOPE_FILTER_ORDER} from '@internal/components/roles-permissions/utils';
import type {RoleListItem} from '@internal/components/roles-permissions/types';
import {Plus, RefreshCw, UserCog, Loader2} from 'lucide-react';

type RolesFilters = {scope: string};

const scopeOptions = SCOPE_FILTER_ORDER.filter((s) => s !== 'all').map((s) => ({
  value: s,
  label: SCOPE_LABELS[s as PermissionScope] ?? s,
}));

const roleColumns: Column<RoleListItem>[] = [
  {
    id: 'name',
    header: 'Role',
    render: (r) => (
      <div className='flex flex-col gap-0.5'>
        <span className='text-sm font-semibold text-brand-navy'>{r.name}</span>
        {r.description && (
          <span className='text-xs text-brand-gray line-clamp-1 max-w-md'>{r.description}</span>
        )}
      </div>
    ),
  },
  {
    id: 'scope',
    header: 'Who is this for?',
    render: (r) => <ScopeBadge scope={r.scope} />,
  },
  {
    id: 'client',
    header: 'Client',
    render: (r) => (
      <span className='text-sm text-brand-navy'>
        {r.client ? r.client.name : <span className='text-brand-gray'>—</span>}
      </span>
    ),
  },
  {
    id: 'permissions',
    header: 'Permissions',
    render: (r) => {
      const count = r._count.permissions;
      return (
        <span className='text-sm text-brand-navy'>
          {count} {count === 1 ? 'permission' : 'permissions'}
        </span>
      );
    },
  },
  {
    id: 'profiles',
    header: 'Users assigned',
    render: (r) => {
      const count = r.profileCount;
      return (
        <span className='text-sm text-brand-navy'>
          {count} {count === 1 ? 'user' : 'users'}
        </span>
      );
    },
  },
];

export function RolesList({embedded = false}: {embedded?: boolean} = {}) {
  const navigate = useNavigate();

  const table = useDataTable<RolesFilters>({
    defaultPageSize: 50,
    defaultFilters: {scope: ''},
    searchDebounce: 400,
    syncToUrl: 'roles',
  });

  const {data, isLoading, isFetching, error, refetch} = trpc.internal.roles.list.useQuery({
    page: table.page,
    pageSize: table.pageSize,
    search: table.debouncedSearch.trim() ? table.debouncedSearch.trim() : undefined,
    scope: table.filterValues.scope ? (table.filterValues.scope as PermissionScope) : undefined,
  });

  const dataTable = (
    <DataTable<RoleListItem, RolesFilters>
      table={table}
      columns={roleColumns}
      data={data?.roles}
      total={data?.totalCount}
      totalPages={data?.totalPages}
      loading={isLoading}
      error={error}
      emptyMessage='No roles match the current filters.'
      title='All roles'
      entity={{singular: 'role', plural: 'roles'}}
      searchPlaceholder='Search by name or description'
      filters={[
        {
          key: 'scope',
          label: 'User types',
          type: 'select',
          placeholder: 'Who is this for?',
          options: scopeOptions,
        },
      ]}
      rowKey={(r) => r.id}
      rowHref={(r) => `/roles/${r.id}`}
    />
  );

  const body = (
    <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
      {embedded && (
        <div className='flex items-center justify-end gap-2 mb-4'>
          {isFetching && !isLoading && <Loader2 className='h-4 w-4 animate-spin text-brand-gray' />}
          <Button variant='outline' onClick={() => refetch()}>
            <RefreshCw className='h-4 w-4 mr-2' />
            Refresh
          </Button>
          <Button onClick={() => navigate('/roles/new')}>
            <Plus className='h-4 w-4 mr-2' />
            New role
          </Button>
        </div>
      )}
      {dataTable}
    </div>
  );

  if (embedded) return body;

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Roles'
          description='Group permissions into roles, then assign roles to users. Built-in roles come pre-configured; create custom roles to tailor access for your team.'
          icon={<UserCog className='h-6 w-6' />}
          isFetching={isFetching}
          isLoading={isLoading}
        >
          <PageHeaderAction tone='secondary' onClick={() => refetch()}>
            <RefreshCw className='h-4 w-4 mr-2' />
            Refresh
          </PageHeaderAction>
          <PageHeaderAction onClick={() => navigate('/roles/new')}>
            <Plus className='h-4 w-4 mr-2' />
            New role
          </PageHeaderAction>
        </PageHeader>

        {body}
      </div>
    </div>
  );
}
